import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import SlideOutDrawer from '../../../components/SlideOutDrawer';
import { palette } from '../../../utils/designTokens';
import {
  fetchProblemRca,
  normalizeProblemRca,
  runProblemRcaAndWait,
  submitProblemRcaFeedback,
} from '../../../services/problemRcaApi';
import RcaDetail from './RcaDetail';
import NoteModal from './NoteModal';
import { errorBannerStyle, infoBannerStyle } from './rcaHelpers';
import { useSessionJwt } from '../../../hooks/useSessionJwt';
import { getMockProblemRca, getMockAnomalyProblemRuns, getMockDriftEvidence } from '../../../services/mockDemoData';
import AnomalyShapeRadars from '../../../components/signals/AnomalyShapeRadars';
import DriftEvidence from '../../../components/signals/DriftEvidence';

const DRAWER_WIDTH_STORAGE_KEY = 'lumoz-rca-drawer-width';
const DEFAULT_DRAWER_WIDTH = 900;
const MIN_DRAWER_WIDTH = 480;
const MAX_DRAWER_WIDTH = 1400;

function clampDrawerWidth(value) {
  return Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, value));
}

function loadSavedDrawerWidth() {
  const saved = Number(localStorage.getItem(DRAWER_WIDTH_STORAGE_KEY));
  return Number.isFinite(saved) && saved > 0 ? clampDrawerWidth(saved) : DEFAULT_DRAWER_WIDTH;
}

/**
 * In-page RCA drawer for the Overview Problems table.
 *
 * This path is problem-native only: it reads/writes `problems`, `problem_rcas`,
 * and `problem_events` through the v2 problem APIs.
 */
export default function RcaDrawer({
  isOpen,
  onClose,
  isDemo = false,
  problemId,
  rcaId,
  appId,
  environment,
  selectedRange,
  onRcaReady,
  onSeeFullAnalysis,
}) {
  const { sessionId, getJwt } = useSessionJwt();
  const [rca, setRca] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [runState, setRunState] = useState({ active: false, message: '', error: '' });
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [thumbsDownModalOpen, setThumbsDownModalOpen] = useState(false);
  const [actionNote, setActionNote] = useState('');
  const [width, setWidth] = useState(loadSavedDrawerWidth);
  const openedKeyRef = useRef('');
  // Business Task Anomaly overlay and Business Task Drift evidence: demo only. Each helper
  // returns data for its one demo problem and nothing for any other.
  const anomalyShapeRuns = useMemo(() => (isDemo ? getMockAnomalyProblemRuns(problemId) : []), [isDemo, problemId]);
  const driftEvidence = useMemo(() => (isDemo ? getMockDriftEvidence(problemId) : null), [isDemo, problemId]);

  const handleResizeDelta = useCallback((delta) => {
    setWidth((prev) => {
      const next = clampDrawerWidth(prev + delta);
      localStorage.setItem(DRAWER_WIDTH_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      openedKeyRef.current = '';
      setRca(null);
      setDetailLoading(false);
      setDetailError(null);
      setRunState({ active: false, message: '', error: '' });
      setActionBusy(false);
      setActionError(null);
      setThumbsDownModalOpen(false);
      setActionNote('');
      return undefined;
    }

    const key = rcaId ? `rca:${rcaId}` : `problem:${problemId || ''}`;
    if (!key || openedKeyRef.current === key) return undefined;
    openedKeyRef.current = key;
    setRca(null);
    setDetailLoading(false);
    setDetailError(null);
    setRunState({ active: false, message: '', error: '' });
    setActionError(null);
    setThumbsDownModalOpen(false);
    setActionNote('');

    if (isDemo) {
      const pid = problemId || (rcaId || '').replace(/^rca-/, '');
      const mockRca = getMockProblemRca(pid);
      if (mockRca) {
        setRca(normalizeProblemRca(mockRca));
        onRcaReady?.(mockRca.rca_id);
      } else {
        setDetailError('RCA is not available for this problem in the demo environment.');
      }
      return undefined;
    }

    const abortController = new AbortController();
    (async () => {
      const jwt = getJwt();
      if (!jwt || !appId || !environment) return;
      if (rcaId) {
        setDetailLoading(true);
        try {
          const detail = await fetchProblemRca(appId, environment, selectedRange, rcaId, jwt, {
            signal: abortController.signal,
            limit: 100,
          });
          if (!abortController.signal.aborted) {
            setRca(normalizeProblemRca(detail));
            onRcaReady?.(detail?.rca_id);
          }
        } catch (err) {
          if (err?.name !== 'AbortError') setDetailError(err?.message || 'Failed to load RCA detail');
        } finally {
          if (!abortController.signal.aborted) setDetailLoading(false);
        }
        return;
      }

      if (problemId) {
        setRunState({ active: true, message: 'Running RCA...', error: '' });
        try {
          const result = await runProblemRcaAndWait(appId, environment, problemId, jwt, {
            signal: abortController.signal,
          });
          const rca = result?.rca || null;
          const generatedRcaId = rca?.rca_id || '';
          if (!abortController.signal.aborted) {
            setRca(normalizeProblemRca(rca || {
              ...(rca || {}),
              problem_id: result?.problem_id || problemId,
            }));
            onRcaReady?.(generatedRcaId);
            setRunState({
              active: false,
              message: result?.timed_out
                ? 'RCA is still running. Refresh status in a moment.'
                : (result?.created ? 'RCA complete.' : 'Loaded existing RCA.'),
              error: '',
            });
          }
        } catch (err) {
          if (!abortController.signal.aborted && err?.name !== 'AbortError') {
            setRunState({ active: false, message: '', error: err?.message || 'Failed to run RCA' });
          }
        }
      }
    })();
    return () => abortController.abort();
  }, [appId, environment, isOpen, isDemo, sessionId, getJwt, onRcaReady, problemId, rcaId, selectedRange]);

  const closeThumbsDownModal = () => {
    if (actionBusy) return;
    setThumbsDownModalOpen(false);
    setActionNote('');
  };

  const handleFeedback = async (vote, note = '') => {
    const currentRcaId = rca?.rca_id;
    const jwt = getJwt();
    if (!currentRcaId || !jwt || actionBusy) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await submitProblemRcaFeedback(appId, environment, currentRcaId, jwt, { vote, note });
      const detail = await fetchProblemRca(appId, environment, selectedRange, currentRcaId, jwt, { limit: 100 });
      setRca(normalizeProblemRca(detail));
      if (vote === 'thumbs_down') setThumbsDownModalOpen(false);
      setActionNote('');
    } catch (err) {
      setActionError(err?.message || 'Failed to save feedback');
    } finally {
      setActionBusy(false);
    }
  };

  const handleSubmitThumbsDown = () => handleFeedback('thumbs_down', actionNote);

  const title = rca?.title || 'RCA';
  const combinedError = runState.error || detailError;

  return (
    <SlideOutDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      width={`${width}px`}
      resizable
      onResizeDelta={handleResizeDelta}
      modal={false}
    >
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
          style={{ color: palette.accents.primary.base }}
          onClick={onSeeFullAnalysis}
        >
          See full analysis <ExternalLink size={11} />
        </button>
      </div>

      {(runState.active || runState.message) && !runState.error && (
        <div className="mb-3 rounded-lg border p-3 text-sm" style={infoBannerStyle()}>
          {runState.message || 'Running RCA...'}
        </div>
      )}

      {combinedError && (
        <div className="mb-3 rounded-lg border p-3 text-sm" style={errorBannerStyle()}>
          {combinedError}
        </div>
      )}

      {actionError && (
        <div className="mb-3 rounded-lg border p-3 text-sm" style={errorBannerStyle()}>
          {actionError}
        </div>
      )}

      {detailLoading ? (
        <div className="rounded-lg border border-border-subtle bg-background-secondary p-4 text-sm text-text-muted">
          Loading RCA detail...
        </div>
      ) : rca ? (
        <RcaDetail
          rca={rca}
          appId={appId}
          environment={environment}
          selectedRange={selectedRange}
          jwt={getJwt()}
          actionBusy={actionBusy}
          onFeedback={handleFeedback}
          onThumbsDown={() => { setActionNote(''); setThumbsDownModalOpen(true); }}
          onResolution={() => {}}
          inlineTracePreview
          linkTarget="newTab"
          showResolution={false}
          evidenceChart={
            driftEvidence ? <DriftEvidence evidence={driftEvidence} />
              : anomalyShapeRuns.length > 0 ? <AnomalyShapeRadars runs={anomalyShapeRuns} />
              : null
          }
        />
      ) : !runState.active && !runState.message && !combinedError ? (
        <div className="rounded-lg border border-border-subtle bg-background-secondary p-4 text-sm text-text-muted">
          No RCA data to show.
        </div>
      ) : null}

      {thumbsDownModalOpen && (
        <NoteModal
          busy={actionBusy}
          note={actionNote}
          onNoteChange={setActionNote}
          onClose={closeThumbsDownModal}
          onSubmit={handleSubmitThumbsDown}
          title="What did we get wrong?"
          description="Add an optional note so we can understand why this RCA was not useful."
          tone="danger"
        />
      )}
    </SlideOutDrawer>
  );
}
